import { FiTrash2 } from 'react-icons/fi';
import { formatDate } from '../../../../utlis';
import MyTable from '../../../../components/MyTable';

// Map API status value to UI status value ('Active' or 'Inactive')
const mapApiStatusToUI = (status, isActive) => {
  if (status !== undefined && status !== null) {
    const statusStr = String(status).toLowerCase();
    if (statusStr === 'active' || statusStr === '1' || statusStr === 'true') {
      return 'Active';
    }
    if (statusStr === 'inactive' || statusStr === '0' || statusStr === 'false') {
      return 'Inactive';
    }
    if (status === 'Active' || status === 'Inactive') {
      return status;
    }
  }
  if (isActive !== undefined && isActive !== null) {
    return isActive === true || isActive === 1 || isActive === '1' || isActive === 'true' ? 'Active' : 'Inactive';
  }
  return 'Active';
};

const UsersTable = ({ data, onEditUser, onDeleteUser, pageSize, onPageSizeChange, roleOptions = [] }) => {
  // Resolve role value to display name using roleOptions
  const resolveRoleName = (roleValue) => {
    if (!roleValue) return '';
    const roleStr = String(roleValue);
    const matched = roleOptions.find(
      (opt) => String(opt.value) === roleStr || opt.text?.toLowerCase() === roleStr.toLowerCase()
    );
    return matched ? matched.text : roleValue;
  };

  // Transform data to ensure consistent field names
  // User Name = first_name + last_name, User ID = user_name
  const transformedData = data.map((user) => {
    const rawRole = user.role || user.role_name || user.user_role || user.role_id || '';
    return {
      id: user.user_id || user.userId || user.id || '',
      user_name: user.user_name || user.username || user.userName || '',
      full_name: (user.first_name && user.last_name)
        ? `${user.first_name} ${user.last_name}`.trim()
        : user.first_name || user.firstName || user.last_name || user.lastName || user.name || '',
      email: user.email_id || user.email || user.user_email || user.emailId || '',
      login_type: user.login_type || user.loginType || user.auth_type || '',
      role: resolveRoleName(rawRole),
      created_by: user.created_by || user.createdBy || '',
      created_at: user.created_at || user.createdAt || user.created || user.dateCreated || '',
      updated_by: user.updated_by || user.updatedBy || '',
      updated_at: user.updated_at || user.updatedAt || user.updated || user.dateUpdated || '',
      status: mapApiStatusToUI(user.status, user.is_active),
      is_active: user.is_active || user.isActive || user.status === 'Active',
    };
  });

  const columns = [
    {
      name: 'id',
      label: 'ID',
      options: {
        display: false,
      },
    },
    {
      name: 'full_name',
      label: 'User Name',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value, tableMeta) => {
          const id = tableMeta.rowData[0];
          return (
            <h3
              onClick={() => onEditUser(id)}
              className="text-base font-bold text-auxiliary-800 cursor-pointer"
            >
              {value || 'N/A'}
            </h3>
          );
        },
      },
    },
    {
      name: 'user_name',
      label: 'User ID',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (
          <p className="text-text-hint">{value || 'N/A'}</p>
        ),
      },
    },
    {
      name: 'email',
      label: 'Email ID',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || 'N/A',
      },
    },
    {
      name: 'login_type',
      label: 'Login Type',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const loginTypeMap = {
            'userid': 'User ID/Password',
            'user_id': 'User ID/Password',
            'user id': 'User ID/Password',
            'password': 'User ID/Password',
            'regular': 'User ID/Password',
            'email': 'Google OAuth',
            'google': 'Google OAuth',
            'google_oauth': 'Google OAuth',
            'oauth': 'Google OAuth',
            'sso': 'SSO',
          };
          return loginTypeMap[value?.toLowerCase()] || value || 'N/A';
        },
      },
    },
    {
      name: 'role',
      label: 'Role',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || 'N/A',
      },
    },
    {
      name: 'created_by',
      label: 'Created By',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || 'N/A',
      },
    },
    {
      name: 'created_at',
      label: 'Date Created',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (value ? formatDate(value) : 'N/A'),
      },
    },
    {
      name: 'updated_by',
      label: 'Updated By',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => value || 'N/A',
      },
    },
    {
      name: 'updated_at',
      label: 'Updated Date',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => (value ? formatDate(value) : 'N/A'),
      },
    },
    {
      name: 'status',
      label: 'Status',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value) => {
          const isActive = value === 'Active';
          return (
            <p
              className={`py-1 px-3 rounded-full text-center text-xs border ${
                isActive
                  ? 'border-success-700 text-success-700'
                  : 'border-gray-500 text-gray-500'
              }`}
            >
              {value || 'N/A'}
            </p>
          );
        },
      },
    },
    {
      name: 'is_active',
      label: 'Is Active',
      options: {
        display: false,
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value, tableMeta) => {
          const userId = tableMeta.rowData[0];
          return (
            <button
              onClick={() => onDeleteUser(userId)}
              className="p-2 hover:bg-red-100 rounded-full"
              title="Delete User"
            >
              <FiTrash2 size={16} className="text-red-600" />
            </button>
          );
        },
      },
    },
  ];

  const options = {
    filterType: 'dropdown',
    responsive: 'standard',
    rowsPerPage: pageSize,
    rowsPerPageOptions: [5, 10, 15, 20, 50],
    onChangeRowsPerPage: (numberOfRows) => onPageSizeChange(numberOfRows),
    selectableRows: 'none',
    elevation: 0,
    download: true,
    print: true,
    search: false, // We have our own search
    viewColumns: true,
    filter: true,
  };

  return (
    <div className="w-full overflow-x">
      <MyTable
        title=""
        data={transformedData}
        columns={columns}
        options={options}
      />
    </div>
  );
};

export default UsersTable;
